import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import bookingPublicService from '../../services/bookingPublicService';

const BookingTerms = () => {
  const { orgCode } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [orgConfig, setOrgConfig] = useState(null);
  const [terms, setTerms] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [configRes, termsRes] = await Promise.all([
          bookingPublicService.getOrgConfig(orgCode),
          bookingPublicService.getTerms(orgCode),
        ]);

        setOrgConfig(configRes);

        if (termsRes.found) {
          setTerms(termsRes.terms);
        } else {
          setError('Terms and conditions not found');
        }
      } catch (err) {
        console.error('Failed to load terms:', err);
        setError(err.response?.data?.error || 'Failed to load terms');
      } finally {
        setLoading(false);
      }
    };

    if (orgCode) {
      loadData();
    }
  }, [orgCode]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" color="error" gutterBottom>
            Terms Unavailable
          </Typography>
          <Typography color="text.secondary" paragraph>
            {error}
          </Typography>
          <Button
            component={Link}
            to={`/book/${orgCode}`}
            startIcon={<ArrowBackIcon />}
            variant="outlined"
          >
            Back to Booking
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: '#f5f5f5',
        py: 4,
      }}
    >
      <Container maxWidth="md">
        {/* Header */}
        <Box textAlign="center" mb={4}>
          {orgConfig?.logo_url && (
            <Box mb={2}>
              <img
                src={orgConfig.logo_url}
                alt={orgConfig.name}
                style={{ maxHeight: 80, maxWidth: 200 }}
              />
            </Box>
          )}
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {terms?.title || 'Terms and Conditions'}
          </Typography>
          <Typography color="text.secondary">
            {orgConfig?.name}
          </Typography>
        </Box>

        {/* Terms Content */}
        <Paper sx={{ p: 4, mb: 3 }}>
          <Box
            dangerouslySetInnerHTML={{ __html: terms?.content || '' }}
            sx={{
              '& h1, & h2, & h3, & h4, & h5, & h6': {
                mt: 3,
                mb: 2,
              },
              '& p': {
                mb: 2,
              },
              '& ul, & ol': {
                mb: 2,
                pl: 3,
              },
              '& li': {
                mb: 1,
              },
            }}
          />
        </Paper>

        {/* Back Button */}
        <Box textAlign="center">
          <Button
            component={Link}
            to={`/book/${orgCode}`}
            startIcon={<ArrowBackIcon />}
            variant="contained"
            sx={{
              backgroundColor: orgConfig?.primary_color || '#1976d2',
              '&:hover': {
                backgroundColor: orgConfig?.primary_color || '#1976d2',
                filter: 'brightness(0.9)',
              },
            }}
          >
            Back to Booking Form
          </Button>
        </Box>

        {/* Last Updated */}
        {terms?.updated_at && (
          <Box textAlign="center" mt={3}>
            <Typography variant="caption" color="text.secondary">
              Last updated: {new Date(terms.updated_at).toLocaleDateString('en-GB', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </Typography>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default BookingTerms;
